import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';

import { interval, Subscription } from 'rxjs';

import { FadeOutAnimation } from '../../animations/fade-out.animation';
import { ArchiveDNSResultsService } from '../../services/archive-dns-results.service';
import { DecisionTransportService } from '../../services/decision-transport.service';
import { InstructionService } from '../../services/instruction.service';
import { LoadConfigService } from '../../services/load-config.service';
import { LoadNeedsphereService } from '../../services/load-needsphere.service';
import { LoadStimuliService } from '../../services/load-stimuli.service';
import { StudyNavigationService } from '../../services/study-navigation.service';

@Component({
    selector: 'app-test-display',
    templateUrl: './test-display.component.html',
    styleUrls: ['./test-display.component.scss'],
    animations: FadeOutAnimation,
    standalone: false
})
export class TestDisplayComponent implements OnInit, OnDestroy {
  @Input() intermissionButtonLabel: string;
  showIntermission = false;
  intermissionPoints = [];

  stimuli: string[];
  keyLabels: string[];
  decisionKeys: string[];
  // timer
  showTimer = false;
  timer: number;
  timerSub: Subscription;

  // information about block:
  blockName: string;
  blockNr: number;
  practiceMode = false;
  readingMode = false;
  // brandImagesId: number = 0;
  // brandImages = [];
  brandImage = undefined;
  brandName = undefined;
  brandImageUrl = undefined;

  // sets the type between default, brand and agreement
  type = 'persona';
  mode = 'IAT';

  // used to iterate through needs
  id = 0;
  finishedTrial = false;

  currentTime: number;
  runningTimer;

  // flag for automatically proceeding to next item in needs array
  dontProceed = false;

  constructor(
    private loadConfigService: LoadConfigService,
    private loadNeedsphereService: LoadNeedsphereService,
    private loadStimuli: LoadStimuliService,
    private archiveDNSResultsService: ArchiveDNSResultsService,
    private decisionTransporter: DecisionTransportService,
    private navigationService: StudyNavigationService,
    private instructionService: InstructionService,
    @Inject('BASE_URL') private baseUrl: string
  ) {}

  ngOnInit(): void {
    // needed to make decision via mouse work
    this.decisionTransporter.decisionEvent.subscribe((decision: number | string) => {
      this.nextItem(decision);
    });
    // this.brandImages = this.loadNeedsphereService.getBrandImages();
    // get all relevant data for saving the results

    this.getIATConfig();

    this.archiveDNSResultsService.initializeArchivedIATResults({
      mode: this.mode,
      IATtype: this.type,
      blockID: this.blockNr,
      brandImage: this.brandImage ? this.brandImage : undefined,
      brandName: this.brandName ? this.brandName : undefined
    });

    // Set the decision Keys
    this.setKeys();
    // Set the needsphere terms
    this.setNeeds();

    this.getIntermissionPoints();

    if (!this.intermissionButtonLabel) {
      this.intermissionButtonLabel = this.instructionService.getTranslationsForTerm('INTERMISSION_CONTINUE_BUTTON');
    }
  }

  ngOnDestroy() {
    // this.IATConfigSub.unsubscribe();
    clearInterval(this.runningTimer);
  }

  getIntermissionPoints() {
    if (['persona', 'brand', 'modular'].includes(this.type) && !this.practiceMode && !this.readingMode) {
      const repetitions = this.loadNeedsphereService.getRepetitions();

      const amountOfStimuli = this.stimuli.length;

      if (repetitions >= 4) {
        const sectionStops = 10 / 3;

        let intermissionBreaks = [];

        let i = sectionStops;
        while (i < repetitions) {
          intermissionBreaks.push(i);
          i += sectionStops;
        }

        // need to check if we went over repetitions (might be due to floating point arithmetic)
        if (intermissionBreaks[intermissionBreaks.length - 1] > repetitions) {
          intermissionBreaks = intermissionBreaks.slice(0, intermissionBreaks.length - 1);
        }

        this.intermissionPoints = intermissionBreaks.map(point => Math.floor(point * (amountOfStimuli / repetitions)));
      }
    }
  }

  startIntermission() {
    clearInterval(this.runningTimer);
    this.showIntermission = true;
  }

  stopIntermission() {
    this.showTimer = true;
    this.timer = 3;
    this.timerSub = interval(1000).subscribe(() => {
      this.timer--;
      if (this.timer === 0) {
        this.showTimer = false;
        this.showIntermission = false;
        this.startTimer();
        if (!this.dontProceed) {
          this.resetTimer();
        }

        this.timerSub.unsubscribe();
      }
    });
    // // trial is finished
    // if (this.id === this.stimuli.length) {
    //   this.finishedTrial = true;
    //   clearInterval(this.runningTimer);
    //   this.navigationService.renderNext();
    // }
  }

  private setKeys() {
    if (this.type === 'agreement') {
      this.decisionKeys = this.loadConfigService.getDecisionKeysAgreement();
    } else {
      this.decisionKeys = this.loadConfigService.getDecisionKeys();
    }
  }

  private setNeeds() {
    this.stimuli = this.loadStimuli.getStimuli(
      this.type,
      this.readingMode,
      this.practiceMode,
      this.mode,
      this.useCustomTerms,
      this.instructionService.getLanguageIdentifier()
    );

    this.currentTime = this.startTimer();

    if (!this.dontProceed) {
      this.resetTimer();
    }
  }
  useCustomTerms = false;
  private getIATConfig() {
    const IATConfig = this.navigationService.IATconfig;
    this.practiceMode = IATConfig.practiceMode;
    this.readingMode = IATConfig.readingMode;
    // we need to disable automatic skipping to next word when in reading mode
    this.useCustomTerms = IATConfig.useCustomTerms;
    this.type = IATConfig.type;
    this.mode = IATConfig.mode;
    this.blockName = IATConfig.blockName;
    this.blockNr = IATConfig.blockNr;

    if (this.type === 'brand') {
      this.brandImage = IATConfig.brandImage;
      this.brandName = IATConfig.brandName;
      this.brandImageUrl = IATConfig.brandImageUrl;
    }

    if (this.mode === 'reading') {
      this.dontProceed = true;
    }

    if ((this.mode === 'IAT' || this.mode === 'practice') && IATConfig['proceedAutomatically'] === false) {
      this.dontProceed = true;
    }
  }

  /**
   * Shows the next item and saves the time it took for the next item to show up.
   */
  nextItem = (decision: number | string, receivedUserInput = true) => {
    if (this.id < this.stimuli.length) {
      this.saveTime(decision, receivedUserInput);
      this.id++;

      if (this.intermissionPoints.includes(this.id)) {
        return this.startIntermission();
      }

      this.startTimer();

      // if (!this.readingMode) {
      //   this.resetTimer();
      // }

      if (!this.dontProceed) {
        this.resetTimer();
      }

      // trial is finished
      if (this.id === this.stimuli.length) {
        this.finishedTrial = true;
        clearInterval(this.runningTimer);
        this.navigationService.renderNext();
      }
    } else {
      // trial is finished
      clearInterval(this.runningTimer);
      this.finishedTrial = true;
    }
  };

  private startTimer = () => {
    const time = new Date().getTime();
    this.currentTime = time;
    return time;
  };

  private endTimer = () => {
    return new Date().getTime() - this.currentTime;
  };

  private saveTime = (decision: number | string, receivedUserInput: boolean) => {
    const reactionTime = this.endTimer();
    this.archiveDNSResultsService.saveIATDecision({
      IATtype: this.type,
      mode: this.mode,
      stimulus: this.stimuli[this.id],
      reactionTime,
      decision: this.mode === 'reading' ? 'continue' : decision,
      receivedUserInput
    });
  };

  private setTimer(time: number, decision: number | string, receivedUserInput: boolean): void {
    this.runningTimer = setInterval(() => {
      this.nextItem(decision, receivedUserInput);
    }, time);
  }

  private resetTimer = () => {
    if (this.runningTimer) {
      clearInterval(this.runningTimer);
    }
    this.setTimer(this.loadConfigService.getProceedAfter(), 0, false);
  };

  getImagePath(imageName: string) {
    if (this.brandImageUrl) return this.brandImageUrl;
    else return `${this.baseUrl}assets/images/${imageName}`;
  }
}
